<template>
    <div :class="$style.container">
        <!-- 통계 탭 네비게이션 -->
        <StatsTabs />

        <!-- 로딩 상태 -->
        <div v-if="loading">
            <LoadingScreen loading-text="활동 통계를 불러오는 중..." />
        </div>

        <!-- 에러 상태 -->
        <div v-else-if="error" :class="$style.error">
            <p>{{ error }}</p>
            <button @click="fetchActivityStats" :class="$style.retryButton">다시 시도</button>
        </div>

        <!-- 통계 데이터 -->
        <div v-else>
            <div :class="$style.header">
                <h1 :class="$style.title">📊 활동 통계</h1>
                <p :class="$style.subtitle">사용자 활동 로그, 로그인 히스토리</p>
            </div>

            <!-- 사용자 선택 및 필터 옵션 -->
            <div :class="$style.filterBar">
                <div :class="$style.userSelector">
                    <label>사용자 선택:</label>
                    <div :class="$style.dropdown" v-click-outside="closeDropdown">
                        <div :class="$style.dropdownTrigger" @click="toggleDropdown">
                            <span :class="$style.dropdownValue">
                                {{ selectedUserLabel }}
                            </span>
                            <span :class="[$style.dropdownArrow, { [$style.open]: isDropdownOpen }]">▼</span>
                        </div>
                        <div v-if="isDropdownOpen" :class="$style.dropdownMenu">
                            <input v-model="searchQuery" :class="$style.dropdownSearch" placeholder="사용자 검색..."
                                @click.stop />
                            <div :class="$style.dropdownList">
                                <div :class="[$style.dropdownItem, { [$style.selected]: selectedUser === '' }]"
                                    @click="selectUser('')">
                                    전체 사용자 요약
                                </div>
                                <div :class="[$style.dropdownItem, $style.anonymousItem, { [$style.selected]: selectedUser === 'anonymous' }]"
                                    @click="selectUser('anonymous')">
                                    👤 비로그인 사용자 활동
                                </div>
                                <div v-for="user in filteredUsers" :key="user.email"
                                    :class="[$style.dropdownItem, { [$style.selected]: selectedUser === user.email }]"
                                    @click="selectUser(user.email)">
                                    <span :class="$style.dropdownNickname">{{ user.nickname || user.email }}</span>
                                    <span :class="$style.dropdownEmail">{{ user.email }}</span>
                                    <span v-if="user.auth === 'admin'" :class="$style.adminBadge">관리자</span>
                                </div>
                                <div v-if="filteredUsers.length === 0" :class="$style.dropdownEmpty">
                                    검색 결과가 없습니다
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <label :class="$style.excludeAdminLabel">
                    <input type="checkbox" v-model="excludeAdmin" @change="onExcludeAdminChange" />
                    <span>관리자 제외</span>
                </label>
            </div>

            <!-- 전체 요약 (사용자 미선택 시) -->
            <div v-if="!selectedUser" :class="$style.summary">
                <div :class="$style.summaryCard">
                    <h3>총 로그인 횟수</h3>
                    <p :class="$style.summaryNumber">{{ totalLoginCount }}</p>
                </div>
                <div :class="$style.summaryCard">
                    <h3>총 페이지뷰</h3>
                    <p :class="$style.summaryNumber">{{ totalPageViews }}</p>
                </div>
                <div :class="$style.summaryCard">
                    <h3>평균 체류 시간</h3>
                    <p :class="$style.summaryNumber">{{ formatDuration(avgDuration) }}</p>
                </div>
                <div :class="$style.summaryCard">
                    <h3>활동 기록 있는 유저</h3>
                    <p :class="$style.summaryNumber">{{ usersWithActivity }}</p>
                </div>
            </div>

            <!-- 익명 사용자 활동 -->
            <div v-else-if="selectedUser === 'anonymous'" :class="$style.anonymousSection">
                <!-- 기간 선택 -->
                <div :class="$style.anonymousFilterBar">
                    <div :class="$style.periodSelector">
                        <label>조회 기간:</label>
                        <select v-model="anonymousDays" @change="fetchAnonymousActivityData"
                            :class="$style.periodSelect">
                            <option :value="1">오늘</option>
                            <option :value="7">최근 7일</option>
                            <option :value="14">최근 14일</option>
                            <option :value="30">최근 30일</option>
                            <option :value="60">최근 60일</option>
                            <option :value="90">최근 90일</option>
                        </select>
                    </div>
                    <button @click="fetchAnonymousActivityData" :class="$style.refreshButton">
                        🔄 새로고침
                    </button>
                </div>

                <!-- 로딩 상태 -->
                <div v-if="anonymousLoading" :class="$style.anonymousLoading">
                    익명 사용자 활동을 불러오는 중...
                </div>

                <!-- 통계 데이터 -->
                <div v-else-if="anonymousStats">
                    <!-- 요약 카드 -->
                    <div :class="$style.anonymousSummary">
                        <div :class="$style.summaryCard">
                            <h3>총 활동 수</h3>
                            <p :class="$style.summaryNumber">{{ anonymousStats.total || 0 }}</p>
                        </div>
                        <div :class="$style.summaryCard">
                            <h3>고유 세션 수</h3>
                            <p :class="$style.summaryNumber">{{ anonymousStats.uniqueSessions || 0 }}</p>
                        </div>
                    </div>

                    <!-- 상세 통계 그리드 -->
                    <div :class="$style.anonymousStatsGrid">
                        <!-- 활동 유형별 -->
                        <div :class="$style.anonymousStatsCard">
                            <h4>📊 활동 유형별</h4>
                            <div :class="$style.statsList">
                                <div v-for="(count, type) in anonymousStats.byType" :key="type"
                                    :class="$style.statsItem">
                                    <span :class="$style.statsLabel">{{ type }}</span>
                                    <span :class="$style.statsValue">{{ count }}회</span>
                                </div>
                                <div v-if="Object.keys(anonymousStats.byType || {}).length === 0"
                                    :class="$style.emptyState">
                                    데이터가 없습니다
                                </div>
                            </div>
                        </div>

                        <!-- 기기별 -->
                        <div :class="$style.anonymousStatsCard">
                            <h4>📱 기기별</h4>
                            <div :class="$style.statsList">
                                <div v-for="(count, device) in anonymousStats.byDevice" :key="device"
                                    :class="$style.statsItem">
                                    <span :class="$style.statsLabel">{{ device }}</span>
                                    <span :class="$style.statsValue">{{ count }}회</span>
                                </div>
                            </div>
                        </div>

                        <!-- 브라우저별 -->
                        <div :class="$style.anonymousStatsCard">
                            <h4>🌐 브라우저별</h4>
                            <div :class="$style.statsList">
                                <div v-for="(count, browser) in anonymousStats.byBrowser" :key="browser"
                                    :class="$style.statsItem">
                                    <span :class="$style.statsLabel">{{ browser }}</span>
                                    <span :class="$style.statsValue">{{ count }}회</span>
                                </div>
                            </div>
                        </div>

                        <!-- 대상별 -->
                        <div :class="$style.anonymousStatsCard">
                            <h4>🎯 대상별</h4>
                            <div :class="$style.statsList">
                                <div v-for="(count, target) in anonymousStats.byTarget" :key="target"
                                    :class="$style.statsItem">
                                    <span :class="$style.statsLabel">{{ target }}</span>
                                    <span :class="$style.statsValue">{{ count }}회</span>
                                </div>
                                <div v-if="Object.keys(anonymousStats.byTarget || {}).length === 0"
                                    :class="$style.emptyState">
                                    데이터가 없습니다
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- 최근 활동 로그 -->
                    <div :class="$style.anonymousLogsSection">
                        <h3>📋 최근 활동 로그 (최대 100개)</h3>
                        <div :class="$style.anonymousLogsList">
                            <div v-for="log in anonymousData.slice(0, 100)" :key="log.id" :class="$style.logItem">
                                <span :class="$style.logType">{{ log.type }}</span>
                                <span v-if="log.target" :class="$style.logTarget">{{ log.target }}</span>
                                <span v-if="log.targetName || log.targetId" :class="$style.logTargetName">
                                    {{ log.targetName || log.targetId }}
                                </span>
                                <span :class="$style.logDevice">{{ log.device }} / {{ log.browser }}</span>
                                <span :class="$style.logTime">{{ formatDateTime(log.timestamp) }}</span>
                            </div>
                            <div v-if="anonymousData.length === 0" :class="$style.emptyState">
                                활동 로그가 없습니다
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- 선택된 사용자 상세 -->
            <div v-else>
                <!-- 사용자 요약 -->
                <div :class="$style.userSummary">
                    <div :class="$style.userInfo">
                        <div :class="$style.userAvatar">
                            <img v-if="selectedUserData?.profileImage" :src="selectedUserData.profileImage"
                                :alt="selectedUserData.nickname" />
                            <div v-else :class="$style.defaultAvatar">👤</div>
                        </div>
                        <div :class="$style.userDetails">
                            <h2>{{ selectedUserData?.nickname || '사용자' }}</h2>
                            <p>{{ selectedUser }}</p>
                        </div>
                    </div>
                    <div :class="$style.userStats">
                        <div :class="$style.statItem">
                            <span :class="$style.statLabel">로그인 횟수</span>
                            <span :class="$style.statValue">{{ userLoginCount }}</span>
                        </div>
                        <div :class="$style.statItem">
                            <span :class="$style.statLabel">총 페이지뷰</span>
                            <span :class="$style.statValue">{{ userPageViews }}</span>
                        </div>
                        <div :class="$style.statItem">
                            <span :class="$style.statLabel">총 체류 시간</span>
                            <span :class="$style.statValue">{{ formatDuration(userTotalDuration) }}</span>
                        </div>
                    </div>
                </div>

                <!-- 탭 선택 -->
                <div :class="$style.tabContainer">
                    <button v-for="tab in tabOptions" :key="tab.value"
                        :class="[$style.tabButton, { [$style.activeTabBtn]: currentTab === tab.value }]"
                        @click="currentTab = tab.value">
                        {{ tab.label }}
                    </button>
                </div>

                <!-- 로그인 히스토리 -->
                <div v-if="currentTab === 'loginHistory'" :class="$style.section">
                    <div :class="$style.sectionHeader">
                        <h3>🔐 로그인 히스토리</h3>
                    </div>
                    <div :class="$style.tableWrapper">
                        <table :class="$style.table">
                            <thead>
                                <tr>
                                    <th>시간</th>
                                    <th>로그인 방식</th>
                                    <th>기기</th>
                                    <th>브라우저</th>
                                    <th>OS</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="login in loginHistory" :key="login.id">
                                    <td>{{ formatDateTime(login.timestamp) }}</td>
                                    <td>
                                        <span :class="[$style.badge, $style[login.provider] || $style.default]">
                                            {{ getProviderLabel(login.provider) }}
                                        </span>
                                    </td>
                                    <td>{{ login.device || '-' }}</td>
                                    <td>{{ login.browser || '-' }}</td>
                                    <td>{{ login.os || '-' }}</td>
                                </tr>
                                <tr v-if="loginHistory.length === 0">
                                    <td colspan="5" :class="$style.empty">로그인 기록이 없습니다.</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                <!-- 활동 로그 -->
                <div v-if="currentTab === 'activityLogs'" :class="$style.section">
                    <div :class="$style.sectionHeader">
                        <h3>📝 활동 로그</h3>
                        <div :class="$style.activityControls">
                            <select v-model="activityFilter" :class="$style.select">
                                <option value="all">전체</option>
                                <option value="CAT_VIEW">📄 페이지 조회</option>
                                <option value="CAT_LIKE">❤️ 찜하기</option>
                                <option value="CAT_COMMUNITY">💬 커뮤니티 활동</option>
                                <option value="CAT_POST">👍 게시글 인터랙션</option>
                                <option value="CAT_SEARCH">🔍 검색/필터</option>
                                <option value="CAT_CREATE">✏️ 콘텐츠 생성/수정</option>
                                <option value="CAT_PROFILE">👤 마이프로필 활동</option>
                                <option value="CAT_CLICK">🔗 링크 클릭</option>
                                <option value="CAT_NOTI">🔔 알림/캘린더</option>
                                <option value="CAT_SHARE">📤 공유</option>
                            </select>
                            <div :class="$style.viewModeToggle">
                                <button
                                    :class="[$style.viewModeBtn, { [$style.active]: activityViewMode === 'grouped' }]"
                                    @click="activityViewMode = 'grouped'">
                                    📊 그룹
                                </button>
                                <button :class="[$style.viewModeBtn, { [$style.active]: activityViewMode === 'table' }]"
                                    @click="activityViewMode = 'table'">
                                    📋 테이블
                                </button>
                            </div>
                        </div>
                    </div>

                    <!-- 그룹화 뷰 -->
                    <div v-if="activityViewMode === 'grouped'" :class="$style.groupedView">
                        <div :class="$style.groupActions">
                            <button @click="expandAllGroups" :class="$style.groupActionBtn">전체 펼치기</button>
                            <button @click="collapseAllGroups" :class="$style.groupActionBtn">전체 접기</button>
                            <span :class="$style.groupCount">{{ groupedActivityLogs.length }}개 유형</span>
                        </div>
                        <div v-for="group in groupedActivityLogs" :key="group.type" :class="$style.activityGroup">
                            <div :class="$style.groupHeader" @click="toggleGroup(group.type)">
                                <span :class="$style.groupToggle">{{ isGroupExpanded(group.type) ? '▼' : '▶' }}</span>
                                <span :class="[$style.badge, getActivityBadgeClass(group.type)]">
                                    {{ group.label }}
                                </span>
                                <span :class="$style.groupCountBadge">{{ group.count }}회</span>
                            </div>
                            <div v-if="isGroupExpanded(group.type)" :class="$style.groupContent">
                                <div v-for="log in group.logs" :key="log.id" :class="$style.groupItem">
                                    <span :class="$style.groupItemTime">{{ formatDateTime(log.timestamp) }}</span>
                                    <span :class="$style.groupItemTarget">{{ log.target || '-' }}</span>
                                    <span :class="$style.groupItemId">{{ log.targetName || log.targetId || '-' }}</span>
                                    <span v-if="log.duration" :class="$style.groupItemDuration">{{
                                        formatDuration(log.duration) }}</span>
                                </div>
                            </div>
                        </div>
                        <div v-if="groupedActivityLogs.length === 0" :class="$style.empty">
                            활동 기록이 없습니다.
                        </div>
                    </div>

                    <!-- 테이블 뷰 -->
                    <div v-else :class="$style.tableWrapper">
                        <table :class="$style.table">
                            <thead>
                                <tr>
                                    <th>시간</th>
                                    <th>활동 유형</th>
                                    <th>대상</th>
                                    <th>대상 이름</th>
                                    <th>체류 시간</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="activity in filteredActivityLogs" :key="activity.id">
                                    <td>{{ formatDateTime(activity.timestamp) }}</td>
                                    <td>
                                        <span :class="[$style.badge, getActivityBadgeClass(activity.type)]">
                                            {{ getActivityLabel(activity.type) }}
                                        </span>
                                    </td>
                                    <td>{{ activity.target || '-' }}</td>
                                    <td>{{ activity.targetName || activity.targetId || '-' }}</td>
                                    <td>{{ activity.duration ? formatDuration(activity.duration) : '-' }}</td>
                                </tr>
                                <tr v-if="filteredActivityLogs.length === 0">
                                    <td colspan="5" :class="$style.empty">활동 기록이 없습니다.</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { collection, getDocs } from "firebase/firestore";
import { firestore } from "/firebaseConfig";
import { useUserStore } from "@/store/user";
import StatsTabs from "@/components/StatsTabs.vue";
import LoadingScreen from "@/components/LoadingScreen.vue";


export default {
    name: "ActivityStats",
    components: {
        StatsTabs,
        LoadingScreen,
    },
    directives: {
        'click-outside': {
            mounted(el, binding) {
                el._clickOutside = (event) => {
                    if (!(el === event.target || el.contains(event.target))) {
                        binding.value();
                    }
                };
                document.addEventListener('click', el._clickOutside);
            },
            unmounted(el) {
                document.removeEventListener('click', el._clickOutside);
            },
        },
    },
    data() {
        return {
            loading: false,
            error: null,
            users: [],
            selectedUser: "",
            currentTab: "loginHistory",
            activityFilter: "all",
            loginHistory: [],
            activityLogs: [],
            tabOptions: [
                { value: "loginHistory", label: "로그인 히스토리" },
                { value: "activityLogs", label: "활동 로그" },
            ],
            // 드롭다운 관련
            isDropdownOpen: false,
            searchQuery: "",
            // 관리자 제외 옵션
            excludeAdmin: true,
            // 전체 요약 집계 데이터
            aggregatedStats: {
                totalLoginCount: 0,
                totalPageViews: 0,
                totalDuration: 0,
                usersWithActivity: 0,
            },
            // 익명 사용자 활동 데이터
            anonymousStats: null,
            anonymousData: [],
            anonymousDays: 30,
            anonymousLoading: false,
            artistNameBySlug: {},
            artistNameLookupLoaded: false,
            // 활동 기록이 있는 사용자 이메일 목록과 최근 활동 시간
            activeUserEmails: new Set(),
            userLastActivity: new Map(), // email -> timestamp
            // 활동 로그 뷰 모드 (table: 테이블, grouped: 그룹화)
            activityViewMode: "grouped",
            // 그룹화 뷰에서 펼쳐진 그룹
            expandedGroups: new Set(),
        };
    },
    computed: {
        selectedUserData() {
            return this.users.find(u => u.email === this.selectedUser);
        },

        // 개별 사용자 통계 (서브컬렉션 기반)
        userLoginCount() {
            return this.loginHistory.length;
        },
        userPageViews() {
            return this.activityLogs.filter(log => log.type?.includes('VIEW')).length;
        },
        userTotalDuration() {
            return this.activityLogs.reduce((sum, log) => sum + (log.duration || 0), 0);
        },

        // 전체 요약 통계 (aggregated data 기반)
        totalLoginCount() {
            return this.aggregatedStats.totalLoginCount;
        },
        totalPageViews() {
            return this.aggregatedStats.totalPageViews;
        },
        avgDuration() {
            const total = this.aggregatedStats.totalDuration;
            const count = this.aggregatedStats.usersWithActivity;
            return count > 0 ? Math.floor(total / count) : 0;
        },
        usersWithActivity() {
            return this.aggregatedStats.usersWithActivity;
        },

        // 필터링된 활동 로그
        filteredActivityLogs() {
            if (this.activityFilter === "all") {
                return this.activityLogs;
            }

            // 카테고리별 필터링
            const categoryFilters = {
                CAT_VIEW: ["PAGE_VIEW", "LIVE_VIEW", "ARTIST_VIEW", "VENUE_VIEW", "VIDEO_VIEW", "BOARD_VIEW", "PROFILE_VIEW", "CALENDAR_VIEW", "NOTIFICATIONS_VIEW"],
                CAT_LIKE: ["LIVE_LIKE", "LIVE_UNLIKE", "ARTIST_LIKE", "ARTIST_UNLIKE", "LIKE", "UNLIKE"],
                CAT_COMMUNITY: ["BOARD_VIEW", "POST_LIKE", "POST_UNLIKE", "POST_COMMENT", "REPORT", "POST_CREATE", "POST_UPDATE", "POST_SHARE"],
                CAT_POST: ["POST_LIKE", "POST_UNLIKE", "POST_COMMENT", "REPORT", "COMMENT"],
                CAT_SEARCH: ["SEARCH", "DATE_FILTER", "FILTER_APPLY"],
                CAT_CREATE: ["LIVE_CREATE", "LIVE_UPDATE", "ARTIST_CREATE", "ARTIST_UPDATE", "POST_CREATE", "POST_UPDATE"],
                CAT_PROFILE: ["PROFILE_VIEW", "NICKNAME_UPDATE", "X_PROFILE_FETCH", "ARTIST_CARD_UPDATE"],
                CAT_CLICK: ["DETAIL_LINK_CLICK", "MAP_VIEW_CLICK", "TICKET_CLICK"],
                CAT_NOTI: ["NOTIFICATION_CLICK", "NOTIFICATION_READ_ALL", "CALENDAR_LIVE_CLICK"],
                CAT_SHARE: ["LIVE_SHARE", "ARTIST_SHARE", "POST_SHARE", "VIDEO_SHARE", "VENUE_SHARE", "SHARE"],
            };

            const types = categoryFilters[this.activityFilter];
            if (types) {
                return this.activityLogs.filter(log => types.includes(log.type));
            }

            // 개별 타입 필터링 (fallback)
            return this.activityLogs.filter(log => log.type === this.activityFilter);
        },

        // 타입별 그룹화된 활동 로그
        groupedActivityLogs() {
            const groups = {};
            this.filteredActivityLogs.forEach(log => {
                const type = log.type || "UNKNOWN";
                if (!groups[type]) {
                    groups[type] = {
                        type,
                        label: this.getActivityLabel(type),
                        count: 0,
                        logs: [],
                    };
                }
                groups[type].count++;
                groups[type].logs.push(log);
            });
            // count 기준 내림차순 정렬
            return Object.values(groups).sort((a, b) => b.count - a.count);
        },

        // 관리자 제외된 사용자 목록
        nonAdminUsers() {
            if (!this.excludeAdmin) {
                return this.users;
            }
            return this.users.filter(user => user.auth !== "admin");
        },

        // 활동 기록이 있는 사용자만 필터링
        usersWithActivityRecords() {
            return this.nonAdminUsers.filter(user => this.activeUserEmails.has(user.email));
        },

        // 드롭다운 검색 필터 및 정렬
        filteredUsers() {
            let users = this.usersWithActivityRecords;

            // 검색 필터 적용
            if (this.searchQuery) {
                const query = this.searchQuery.toLowerCase();
                users = users.filter(user =>
                    (user.nickname || "").toLowerCase().includes(query) ||
                    (user.email || "").toLowerCase().includes(query)
                );
            }

            // 최근 활동 기준으로 정렬 (최근이 위로)
            return users.sort((a, b) => {
                const aTime = this.userLastActivity.get(a.email) || 0;
                const bTime = this.userLastActivity.get(b.email) || 0;
                return bTime - aTime; // 내림차순 (최근이 먼저)
            });
        },

        // 선택된 사용자 라벨
        selectedUserLabel() {
            if (!this.selectedUser) {
                return "전체 사용자 요약";
            }
            if (this.selectedUser === "anonymous") {
                return "👤 비로그인 사용자 활동";
            }
            const user = this.users.find(u => u.email === this.selectedUser);
            return user ? `${user.nickname || user.email}` : this.selectedUser;
        },
    },
    async mounted() {
        // 접근 권한 체크
        const userStore = useUserStore();
        if (userStore.auth !== 'admin') {
            this.$alert("접근 권한이 없습니다.");
            this.$router.push("/");
            return;
        }

        await this.fetchActivityStats();
    },
    methods: {
        async fetchActivityStats() {
            this.loading = true;
            this.error = null;

            try {
                const { getAllUsers } = await import("@/utils/read/user");
                this.users = await getAllUsers();

                // 닉네임 순으로 정렬
                this.users.sort((a, b) => {
                    const aNickname = (a.nickname || a.email || "").toLowerCase();
                    const bNickname = (b.nickname || b.email || "").toLowerCase();
                    return aNickname.localeCompare(bNickname, "ko");
                });

                // 서브컬렉션 집계 (API 호출)
                await this.aggregateSubcollections();

            } catch (error) {
                console.error("활동 통계 로딩 오류:", error);
                this.error = "활동 통계를 불러오는데 실패했습니다.";
            } finally {
                this.loading = false;
            }
        },

        async aggregateSubcollections() {
            try {
                const { getAggregatedActivity } = await import("@/utils/read/user");

                // 관리자 제외할 이메일 목록
                const excludeAdminEmails = this.excludeAdmin
                    ? this.users.filter(u => u.auth === "admin").map(u => u.email)
                    : [];

                // API 호출하여 집계 데이터 가져오기
                const result = await getAggregatedActivity(excludeAdminEmails);

                // 집계 통계 설정
                this.aggregatedStats = result.aggregatedStats;

                // 활동 기록이 있는 사용자 목록과 최근 활동 시간 저장
                this.activeUserEmails = new Set(result.activeUserEmails);
                this.userLastActivity = new Map(Object.entries(result.userLastActivity));

            } catch (error) {
                console.error("서브컬렉션 집계 오류:", error);
            }
        },

        // 관리자 제외 옵션 변경 시
        async onExcludeAdminChange() {
            // 선택된 사용자가 관리자인 경우 선택 해제
            if (this.excludeAdmin && this.selectedUser) {
                const selectedUserData = this.users.find(u => u.email === this.selectedUser);
                if (selectedUserData && selectedUserData.auth === "admin") {
                    this.selectedUser = "";
                    this.loginHistory = [];
                    this.activityLogs = [];
                }
            }
            // 집계 다시 수행
            await this.aggregateSubcollections();
        },

        // 드롭다운 메서드
        toggleDropdown() {
            this.isDropdownOpen = !this.isDropdownOpen;
            if (this.isDropdownOpen) {
                this.searchQuery = "";
            }
        },

        closeDropdown() {
            this.isDropdownOpen = false;
        },

        // 그룹 토글 메서드
        toggleGroup(type) {
            if (this.expandedGroups.has(type)) {
                this.expandedGroups.delete(type);
            } else {
                this.expandedGroups.add(type);
            }
            // Vue 반응성을 위해 새 Set 생성
            this.expandedGroups = new Set(this.expandedGroups);
        },

        isGroupExpanded(type) {
            return this.expandedGroups.has(type);
        },

        expandAllGroups() {
            this.expandedGroups = new Set(this.groupedActivityLogs.map(g => g.type));
        },

        collapseAllGroups() {
            this.expandedGroups = new Set();
        },

        selectUser(email) {
            this.selectedUser = email;
            this.isDropdownOpen = false;
            this.searchQuery = "";
            this.onUserChange();
        },

        async onUserChange() {
            if (!this.selectedUser) {
                this.loginHistory = [];
                this.activityLogs = [];
                return;
            }

            if (this.selectedUser === "anonymous") {
                await this.fetchAnonymousActivityData();
                return;
            }

            await this.fetchUserActivityData();
        },

        async fetchAnonymousActivityData() {
            this.anonymousLoading = true;
            try {
                const { getAnonymousActivityStats } = await import("@/utils/read/user");
                const result = await getAnonymousActivityStats(this.anonymousDays);
                this.anonymousData = result.data;
                this.anonymousStats = result.stats;
            } catch (error) {
                console.error("익명 활동 데이터 로드 오류:", error);
            } finally {
                this.anonymousLoading = false;
            }
        },

        async fetchUserActivityData() {
            try {
                const { getUserLoginHistory, getUserActivityLogs } = await import("@/utils/read/user");

                // 로그인 히스토리 가져오기
                this.loginHistory = await getUserLoginHistory(this.selectedUser, {
                    limit: 50,
                    orderBy: 'timestamp',
                    order: 'desc'
                });

                // 활동 로그 가져오기 (ARTIST_VIEW targetId slug -> name 보강)
                const rawLogs = await getUserActivityLogs(this.selectedUser, {
                    limit: 100,
                    orderBy: 'timestamp',
                    order: 'desc'
                });
                this.activityLogs = await this.enrichArtistViewTargetNames(rawLogs);

            } catch (error) {
                console.error("사용자 활동 데이터 로딩 오류:", error);
            }
        },

        extractArtistSlug(targetId) {
            if (targetId == null) return null;
            const raw = String(targetId).trim();
            if (!raw) return null;

            const noDomain = raw.replace(/^https?:\/\/[^/]+/i, "");
            const routeMatch = noDomain.match(/\/artist\/([^/?#]+)/i);
            if (routeMatch?.[1]) return routeMatch[1];

            return raw.split("?")[0].split("#")[0];
        },

        async ensureArtistNameLookup() {
            if (this.artistNameLookupLoaded) return;
            try {
                const snapshot = await getDocs(collection(firestore, "artists"));
                const bySlug = {};
                snapshot.docs.forEach((docSnap) => {
                    const artist = docSnap.data() || {};
                    const slug = artist.slug;
                    const name = artist.name || docSnap.id;
                    if (slug != null && name) {
                        bySlug[String(slug)] = name;
                    }
                });
                this.artistNameBySlug = bySlug;
                this.artistNameLookupLoaded = true;
            } catch (error) {
                console.warn("아티스트 slug->name 매핑 로드 실패:", error);
            }
        },

        async enrichArtistViewTargetNames(logs) {
            if (!Array.isArray(logs) || logs.length === 0) return logs || [];
            const hasArtistView = logs.some((log) => log?.type === "ARTIST_VIEW" && log?.targetId != null);
            if (!hasArtistView) return logs;

            await this.ensureArtistNameLookup();
            if (!this.artistNameBySlug || Object.keys(this.artistNameBySlug).length === 0) return logs;

            return logs.map((log) => {
                if (log?.type !== "ARTIST_VIEW" || log?.targetId == null) return log;
                const slug = this.extractArtistSlug(log.targetId);
                if (!slug) return log;
                const resolvedName = this.artistNameBySlug[String(slug)];
                if (!resolvedName) return log;

                // 기존 targetName이 slug 값인 경우도 name으로 교체
                const currentName = log.targetName == null ? "" : String(log.targetName);
                const targetIdStr = String(log.targetId);
                if (!currentName || currentName === targetIdStr) {
                    return { ...log, targetName: resolvedName };
                }
                return log;
            });
        },

        formatDateTime(date) {
            if (!date) return "-";

            try {
                let d;
                if (date.toDate && typeof date.toDate === "function") {
                    d = date.toDate();
                } else if (typeof date === "object" && date.seconds) {
                    d = new Date(date.seconds * 1000);
                } else {
                    d = new Date(date);
                }

                if (isNaN(d.getTime())) return "-";

                return d.toLocaleString("ko-KR", {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                });
            } catch {
                return "-";
            }
        },

        formatDuration(seconds) {
            if (!seconds || seconds <= 0) return "0초";

            const hours = Math.floor(seconds / 3600);
            const minutes = Math.floor((seconds % 3600) / 60);
            const secs = seconds % 60;

            if (hours > 0) {
                return `${hours}시간 ${minutes}분`;
            } else if (minutes > 0) {
                return `${minutes}분 ${secs}초`;
            } else {
                return `${secs}초`;
            }
        },

        getProviderLabel(provider) {
            const labels = {
                kakao: "카카오",
                email: "이메일",
                email_signup: "이메일(가입)",
                twitter: "X",
                google: "구글",
                apple: "애플",
            };
            return labels[provider] || provider || "알 수 없음";
        },

        getActivityLabel(type) {
            const labels = {
                // 페이지 조회
                PAGE_VIEW: "페이지 조회",
                LIVE_VIEW: "라이브 조회",
                ARTIST_VIEW: "아티스트 조회",
                VENUE_VIEW: "공연장 조회",
                VIDEO_VIEW: "영상 조회",
                BOARD_VIEW: "게시글 조회",
                PROFILE_VIEW: "마이프로필 조회",
                CALENDAR_VIEW: "캘린더",
                NOTIFICATIONS_VIEW: "알림 페이지",

                // 찜하기
                LIVE_LIKE: "라이브 찜",
                LIVE_UNLIKE: "라이브 찜 취소",
                ARTIST_LIKE: "아티스트 찜",
                ARTIST_UNLIKE: "아티스트 찜 취소",
                VIDEO_LIKE: "영상 찜",
                VIDEO_UNLIKE: "영상 찜 취소",
                VIDEO_PLAY: "영상 재생(임베드)",

                // 게시글 추천
                POST_LIKE: "게시글 추천",
                POST_UNLIKE: "게시글 추천 취소",
                POST_COMMENT: "댓글 작성",
                REPORT: "신고",

                // 검색/필터
                SEARCH: "검색",
                DATE_FILTER: "날짜 필터",
                FILTER_APPLY: "필터 적용",

                // 콘텐츠 생성/수정
                LIVE_CREATE: "라이브 등록",
                LIVE_UPDATE: "라이브 수정",
                ARTIST_CREATE: "아티스트 등록",
                ARTIST_UPDATE: "아티스트 수정",
                POST_CREATE: "게시글 작성",
                POST_UPDATE: "게시글 수정",

                // 프로필 활동
                NICKNAME_UPDATE: "닉네임 수정",
                X_PROFILE_FETCH: "X 프로필 연동",
                ARTIST_CARD_UPDATE: "아티스트 카드",

                // 링크 클릭
                DETAIL_LINK_CLICK: "자세히보기",
                MAP_VIEW_CLICK: "지도보기",
                TICKET_CLICK: "티켓 클릭",

                // 알림/캘린더 인터랙션
                NOTIFICATION_CLICK: "알림 클릭",
                NOTIFICATION_READ_ALL: "전체 읽음",
                CALENDAR_LIVE_CLICK: "캘린더 공연",

                // 공유
                LIVE_SHARE: "라이브 공유",
                ARTIST_SHARE: "아티스트 공유",
                POST_SHARE: "게시글 공유",
                VIDEO_SHARE: "영상 공유",
                VENUE_SHARE: "공연장 공유",

                // 세션
                LOGIN: "로그인",
                LOGOUT: "로그아웃",
                SESSION_START: "세션 시작",
                SESSION_END: "세션 종료",

                // 기타 (레거시)
                LIKE: "찜 추가",
                UNLIKE: "찜 취소",
                COMMENT: "댓글",
                SHARE: "공유",
            };
            return labels[type] || type;
        },

        getActivityBadgeClass(type) {
            if (type.includes("VIEW") || type === "CALENDAR_VIEW" || type === "NOTIFICATIONS_VIEW") return this.$style.view;
            if (type.includes("LIKE") || type.includes("UNLIKE")) return this.$style.like;
            if (type.includes("SEARCH") || type.includes("FILTER")) return this.$style.search;
            if (type.includes("LOGIN") || type.includes("LOGOUT") || type.includes("SESSION")) return this.$style.login;
            if (type.includes("TICKET")) return this.$style.ticket;
            if (type.includes("CREATE") || type.includes("UPDATE")) return this.$style.create;
            if (type.includes("SHARE")) return this.$style.share;
            if (type.includes("CLICK") || type === "MAP_VIEW_CLICK") return this.$style.click;
            if (type.includes("REPORT")) return this.$style.report;
            if (type.includes("COMMENT")) return this.$style.comment;
            if (type.includes("NICKNAME") || type.includes("PROFILE") || type.includes("CARD")) return this.$style.profile;
            if (type.includes("NOTIFICATION") || type.includes("CALENDAR_LIVE")) return this.$style.notification;
            return this.$style.default;
        },
    },
};
</script>

<style module>
/* ========================================
   반응형 디자인 (최대 1024px)
   - Medium: 481px ~ (기본 스타일)
   - Small: ~ 480px
   ======================================== */

.container {
    width: 100%;
    max-width: 1024px;
    padding: 0 15px;
    margin: 0 auto;
}

.error {
    text-align: center;
    padding: 30px;
    color: #d32f2f;
}

.retryButton {
    background: #2196f3;
    color: white;
    border: none;
    padding: 10px 20px;
    border-radius: 8px;
    cursor: pointer;
    margin-top: 10px;
}

.header {
    text-align: center;
    margin-bottom: 20px;
    padding: 10px 0;
}

.title {
    font-size: 1.5rem;
    font-weight: bold;
    color: #333;
    margin: 0 0 8px 0;
}

.subtitle {
    font-size: 0.85rem;
    color: #666;
    margin: 0;
}

/* 필터 바 */
.filterBar {
    background: white;
    padding: 15px 20px;
    border-radius: 10px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    margin-bottom: 20px;
    display: flex;
    align-items: center;
    gap: 20px;
    flex-wrap: wrap;
}

/* 사용자 선택 */
.userSelector {
    display: flex;
    align-items: center;
    gap: 15px;
    flex: 1;
}

.userSelector label {
    font-weight: 600;
    color: #333;
    white-space: nowrap;
}

/* 관리자 제외 체크박스 */
.excludeAdminLabel {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    color: #666;
    cursor: pointer;
    white-space: nowrap;
}

.excludeAdminLabel input[type="checkbox"] {
    width: 16px;
    height: 16px;
    cursor: pointer;
}

.excludeAdminLabel span {
    user-select: none;
}

/* 관리자 배지 */
.adminBadge {
    font-size: 10px;
    background: #ff6b6b;
    color: white;
    padding: 2px 6px;
    border-radius: 4px;
    margin-left: auto;
}

/* 드롭다운 */
.dropdown {
    flex: 1;
    max-width: 400px;
    position: relative;
}

.dropdownTrigger {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 15px;
    border: 1px solid #ddd;
    border-radius: 8px;
    background: white;
    cursor: pointer;
    transition: all 0.2s;
}

.dropdownTrigger:hover {
    border-color: #2196f3;
}

.dropdownValue {
    font-size: 14px;
    color: #333;
}

.dropdownArrow {
    font-size: 10px;
    color: #888;
    transition: transform 0.2s;
}

.dropdownArrow.open {
    transform: rotate(180deg);
}

.dropdownMenu {
    position: absolute;
    top: calc(100% + 5px);
    left: 0;
    right: 0;
    background: white;
    border: 1px solid #ddd;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    z-index: 100;
    max-height: 350px;
    display: flex;
    flex-direction: column;
}

.dropdownSearch {
    padding: 12px 15px;
    border: none;
    border-bottom: 1px solid #eee;
    font-size: 14px;
    outline: none;
}

.dropdownSearch:focus {
    background: #f9f9f9;
}

.dropdownList {
    overflow-y: auto;
    max-height: 280px;
}

.dropdownItem {
    padding: 12px 15px;
    cursor: pointer;
    transition: background 0.15s;
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.dropdownItem:hover {
    background: #f5f5f5;
}

.dropdownItem.selected {
    background: #e3f2fd;
}

.dropdownNickname {
    font-size: 14px;
    font-weight: 500;
    color: #333;
}

.dropdownEmail {
    font-size: 12px;
    color: #888;
}

.dropdownEmpty {
    padding: 20px;
    text-align: center;
    color: #999;
    font-size: 14px;
}

.select {
    flex: 1;
    max-width: 400px;
    padding: 10px 15px;
    border: 1px solid #ddd;
    border-radius: 8px;
    background: white;
    font-size: 14px;
}

/* 요약 카드 */
.summary {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 10px;
    margin-bottom: 20px;
}

.summaryCard {
    background: white;
    padding: 15px;
    border-radius: 10px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    text-align: center;
    border: 1px solid #e0e0e0;
}

.summaryCard h3 {
    margin: 0 0 8px 0;
    font-size: 0.8rem;
    color: #666;
    font-weight: 600;
}

.summaryNumber {
    font-size: 1.4rem;
    font-weight: bold;
    color: #2196f3;
    margin: 0;
}

/* 익명 사용자 드롭다운 아이템 */
.anonymousItem {
    background: linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%);
    border-left: 3px solid #ff9800;
}

.anonymousItem:hover {
    background: linear-gradient(135deg, #ffe0b2 0%, #ffcc80 100%);
}

/* 익명 사용자 섹션 */
.anonymousSection {
    margin-top: 20px;
}

.anonymousFilterBar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    gap: 15px;
    flex-wrap: wrap;
}

.periodSelector {
    display: flex;
    align-items: center;
    gap: 10px;
}

.periodSelector label {
    font-size: 0.9rem;
    color: #555;
}

.periodSelect {
    padding: 8px 12px;
    border: 1px solid #ddd;
    border-radius: 6px;
    font-size: 0.9rem;
    background: white;
    cursor: pointer;
}

.refreshButton {
    padding: 8px 16px;
    background: #4caf50;
    color: white;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    font-size: 0.85rem;
}

.refreshButton:hover {
    background: #43a047;
}

.anonymousLoading {
    text-align: center;
    padding: 40px;
    color: #666;
    font-size: 0.9rem;
}

.anonymousSummary {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 15px;
    margin-bottom: 25px;
}

.anonymousStatsGrid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 12px;
    margin-bottom: 20px;
}

.anonymousStatsCard {
    background: white;
    padding: 16px;
    border-radius: 10px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    border: 1px solid #e0e0e0;
}

.anonymousStatsCard h4 {
    margin: 0 0 12px 0;
    font-size: 0.9rem;
    color: #333;
    font-weight: 600;
}

.statsList {
    display: flex;
    flex-direction: column;
    gap: 6px;
    max-height: 200px;
    overflow-y: auto;
}

.statsItem {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 6px 8px;
    background: #f8f9fa;
    border-radius: 6px;
    font-size: 0.8rem;
}

.statsLabel {
    color: #333;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.statsValue {
    color: #2196f3;
    font-weight: 600;
    flex-shrink: 0;
}

.emptyState {
    text-align: center;
    color: #999;
    padding: 15px;
    font-size: 0.85rem;
}

.anonymousLogsSection {
    background: white;
    padding: 20px;
    border-radius: 10px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    border: 1px solid #e0e0e0;
}

.anonymousLogsSection h3 {
    margin: 0 0 15px 0;
    font-size: 1rem;
    color: #333;
}

.anonymousLogsList {
    max-height: 400px;
    overflow-y: auto;
}

.logItem {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 12px;
    border-bottom: 1px solid #eee;
    font-size: 0.8rem;
}

.logItem:last-child {
    border-bottom: none;
}

.logType {
    background: #e3f2fd;
    color: #1976d2;
    padding: 3px 8px;
    border-radius: 4px;
    font-weight: 600;
    font-size: 0.75rem;
}

.logTarget {
    background: #f3e5f5;
    color: #7b1fa2;
    padding: 3px 8px;
    border-radius: 4px;
    font-size: 0.75rem;
}

.logTargetName {
    background: #e8f5e9;
    color: #2e7d32;
    padding: 3px 8px;
    border-radius: 4px;
    font-size: 0.75rem;
    font-weight: 500;
}

.logDevice {
    color: #888;
    flex-shrink: 0;
}

.logTime {
    color: #666;
    flex-shrink: 0;
    margin-left: auto;
}

/* 사용자 요약 */
.userSummary {
    background: white;
    padding: 15px;
    border-radius: 10px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    margin-bottom: 15px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 15px;
}

.userInfo {
    display: flex;
    align-items: center;
    gap: 12px;
}

.userAvatar {
    width: 50px;
    height: 50px;
    border-radius: 50%;
    overflow: hidden;
    background: #f5f5f5;
    display: flex;
    align-items: center;
    justify-content: center;
}

.userAvatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.defaultAvatar {
    font-size: 1.3rem;
}

.userDetails h2 {
    margin: 0 0 4px 0;
    font-size: 1.1rem;
    color: #333;
}

.userDetails p {
    margin: 0;
    font-size: 0.8rem;
    color: #888;
}

.userStats {
    display: flex;
    gap: 20px;
    flex-wrap: wrap;
}

.statItem {
    text-align: center;
}

.statLabel {
    display: block;
    font-size: 0.75rem;
    color: #888;
    margin-bottom: 4px;
}

.statValue {
    font-size: 1.1rem;
    font-weight: bold;
    color: #2196f3;
}

/* 탭 */
.tabContainer {
    display: flex;
    gap: 8px;
    margin-bottom: 15px;
    background: white;
    padding: 8px;
    border-radius: 10px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
    flex-wrap: wrap;
}

.tabButton {
    flex: 1;
    padding: 10px 12px;
    border: 1px solid #e0e0e0;
    background: #f8f9fa;
    border-radius: 8px;
    font-size: 0.85rem;
    font-weight: 500;
    color: #666;
    cursor: pointer;
    transition: all 0.2s;
    min-width: 80px;
}

.tabButton:hover {
    background: #f0f0f0;
}

.activeTabBtn {
    background: #2196f3;
    color: white;
    border-color: #2196f3;
}

.activeTabBtn:hover {
    background: #1976d2;
}

/* 섹션 */
.section {
    background: white;
    border-radius: 10px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    overflow: hidden;
    margin-bottom: 15px;
}

.sectionHeader {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 15px;
    background: #f8f9fa;
    border-bottom: 1px solid #e0e0e0;
    flex-wrap: wrap;
    gap: 10px;
}

.sectionHeader h3 {
    margin: 0;
    font-size: 0.95rem;
    color: #333;
}

/* 활동 로그 컨트롤 */
.activityControls {
    display: flex;
    gap: 12px;
    align-items: center;
}

.viewModeToggle {
    display: flex;
    border: 1px solid #ddd;
    border-radius: 6px;
    overflow: hidden;
}

.viewModeBtn {
    padding: 6px 12px;
    background: #fff;
    border: none;
    cursor: pointer;
    font-size: 0.85rem;
    transition: all 0.2s;
}

.viewModeBtn:not(:last-child) {
    border-right: 1px solid #ddd;
}

.viewModeBtn:hover {
    background: #f5f5f5;
}

.viewModeBtn.active {
    background: #4a90d9;
    color: #fff;
}

/* 그룹화 뷰 */
.groupedView {
    padding: 15px;
}

.groupActions {
    display: flex;
    gap: 10px;
    margin-bottom: 15px;
    align-items: center;
}

.groupActionBtn {
    padding: 6px 12px;
    background: #f5f5f5;
    border: 1px solid #ddd;
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.85rem;
    transition: all 0.2s;
}

.groupActionBtn:hover {
    background: #e9e9e9;
}

.groupCount {
    margin-left: auto;
    color: #888;
    font-size: 0.85rem;
}

.activityGroup {
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    margin-bottom: 10px;
    overflow: hidden;
}

.groupHeader {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 15px;
    background: #fafafa;
    cursor: pointer;
    transition: background 0.2s;
}

.groupHeader:hover {
    background: #f0f0f0;
}

.groupToggle {
    color: #666;
    font-size: 0.8rem;
    width: 16px;
}

.groupCountBadge {
    margin-left: auto;
    padding: 3px 10px;
    background: #4a90d9;
    color: #fff;
    border-radius: 12px;
    font-size: 0.8rem;
    font-weight: 500;
}

.groupContent {
    border-top: 1px solid #e0e0e0;
    max-height: 300px;
    overflow-y: auto;
}

.groupItem {
    display: flex;
    gap: 15px;
    padding: 10px 15px;
    border-bottom: 1px solid #f0f0f0;
    font-size: 0.85rem;
}

.groupItem:last-child {
    border-bottom: none;
}

.groupItem:hover {
    background: #fafafa;
}

.groupItemTime {
    color: #888;
    min-width: 130px;
}

.groupItemTarget {
    color: #555;
    min-width: 80px;
}

.groupItemId {
    color: #333;
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.groupItemDuration {
    color: #4a90d9;
    min-width: 70px;
    text-align: right;
}

/* 테이블 */
.tableWrapper {
    overflow-x: auto;
}

.table {
    width: 100%;
    border-collapse: collapse;
}

.table th,
.table td {
    padding: 12px 15px;
    text-align: left;
    border-bottom: 1px solid #eee;
    font-size: 0.9rem;
}

.table th {
    background: #fafafa;
    font-weight: 600;
    color: #555;
}

.table tbody tr:hover {
    background: #f9f9f9;
}

.empty {
    text-align: center;
    color: #999;
    padding: 30px !important;
}

/* 뱃지 */
.badge {
    display: inline-block;
    padding: 4px 10px;
    border-radius: 12px;
    font-size: 0.8rem;
    font-weight: 500;
}

.badge.kakao {
    background: #fee500;
    color: #3c1e1e;
}

.badge.email,
.badge.email_signup {
    background: #607d8b;
    color: white;
}

.badge.twitter {
    background: #1da1f2;
    color: white;
}

.badge.default {
    background: #e0e0e0;
    color: #666;
}

.badge.view {
    background: #e3f2fd;
    color: #1976d2;
}

.badge.like {
    background: #fce4ec;
    color: #c2185b;
}

.badge.search {
    background: #fff3e0;
    color: #e65100;
}

.badge.login {
    background: #e8f5e9;
    color: #388e3c;
}

.badge.ticket {
    background: #f3e5f5;
    color: #7b1fa2;
}

.badge.create {
    background: #e8f5e9;
    color: #2e7d32;
}

.badge.share {
    background: #e1f5fe;
    color: #0277bd;
}

.badge.click {
    background: #fff8e1;
    color: #f57f17;
}

.badge.report {
    background: #ffebee;
    color: #c62828;
}

.badge.comment {
    background: #f1f8e9;
    color: #558b2f;
}

.badge.profile {
    background: #fce4ec;
    color: #ad1457;
}

.badge.notification {
    background: #fff3e0;
    color: #ef6c00;
}

/* ========================================
   Small (~ 480px)
   ======================================== */
@media (max-width: 480px) {
    .container {
        padding: 0 10px;
    }

    .header {
        margin-bottom: 15px;
        padding: 8px 0;
    }

    .title {
        font-size: 1.3rem;
    }

    .subtitle {
        font-size: 0.8rem;
    }

    .filterBar {
        flex-direction: column;
        align-items: stretch;
        gap: 10px;
        padding: 12px;
    }

    .userSelector {
        flex-direction: column;
        align-items: stretch;
    }

    .excludeAdminLabel {
        justify-content: flex-start;
    }

    .dropdown {
        max-width: none;
    }

    .select {
        max-width: none;
    }

    .summary {
        grid-template-columns: repeat(2, 1fr);
        gap: 8px;
    }

    .summaryCard {
        padding: 12px;
    }

    .summaryCard h3 {
        font-size: 0.7rem;
        margin-bottom: 6px;
    }

    .summaryNumber {
        font-size: 1.2rem;
    }

    .userSummary {
        flex-direction: column;
        text-align: center;
        padding: 15px;
    }

    .userStats {
        flex-wrap: wrap;
        justify-content: center;
        gap: 12px;
    }

    .statLabel {
        font-size: 0.7rem;
    }

    .statValue {
        font-size: 1rem;
    }

    .tabContainer {
        flex-direction: column;
        gap: 6px;
        padding: 6px;
    }

    .tabButton {
        padding: 8px 10px;
        font-size: 0.8rem;
    }

    .section {
        margin-bottom: 12px;
    }

    .sectionHeader {
        padding: 10px 12px;
        flex-direction: column;
        align-items: flex-start;
    }

    .sectionHeader h3 {
        font-size: 0.9rem;
    }

    .table th,
    .table td {
        padding: 8px;
        font-size: 0.75rem;
    }

    /* 활동 로그 컨트롤 */
    .activityControls {
        width: 100%;
        flex-direction: column;
        gap: 8px;
    }

    .activityControls .select {
        width: 100%;
    }

    .viewModeToggle {
        width: 100%;
    }

    .viewModeBtn {
        flex: 1;
        padding: 8px 10px;
        font-size: 0.75rem;
    }

    /* 그룹화 뷰 */
    .groupedView {
        padding: 10px;
    }

    .groupActions {
        flex-wrap: wrap;
        gap: 6px;
    }

    .groupActionBtn {
        padding: 5px 8px;
        font-size: 0.7rem;
    }

    .groupCount {
        font-size: 0.7rem;
        width: 100%;
        margin-left: 0;
        margin-top: 4px;
    }

    .activityGroup {
        margin-bottom: 8px;
    }

    .groupHeader {
        padding: 8px 10px;
        gap: 6px;
    }

    .groupHeader .badge {
        font-size: 0.65rem;
        padding: 2px 6px;
    }

    .groupCountBadge {
        font-size: 0.65rem;
        padding: 2px 6px;
    }

    .groupContent {
        max-height: 180px;
    }

    .groupItem {
        flex-wrap: wrap;
        gap: 4px 8px;
        padding: 6px 10px;
        font-size: 0.7rem;
    }

    .groupItemTime {
        min-width: auto;
        width: 100%;
        font-size: 0.65rem;
    }

    .groupItemTarget {
        min-width: auto;
        font-size: 0.7rem;
    }

    .groupItemId {
        font-size: 0.7rem;
        max-width: 120px;
    }

    .groupItemDuration {
        min-width: auto;
        font-size: 0.7rem;
    }

    /* 익명 사용자 섹션 */
    .anonymousFilterBar {
        flex-direction: column;
        align-items: stretch;
        gap: 10px;
    }

    .periodSelector {
        flex-direction: column;
        align-items: stretch;
    }

    .periodSelect {
        width: 100%;
    }

    .refreshButton {
        width: 100%;
    }

    .anonymousSummary {
        grid-template-columns: repeat(2, 1fr);
        gap: 8px;
    }

    .anonymousStatsCard {
        padding: 12px;
    }

    .anonymousStatsCard h4 {
        font-size: 0.8rem;
    }

    .logItem {
        flex-wrap: wrap;
        gap: 4px;
        padding: 8px 10px;
        font-size: 0.75rem;
    }

    .logTime {
        width: 100%;
        margin-left: 0;
    }

    .badge {
        font-size: 0.7rem;
        padding: 3px 8px;
    }
}
</style>
